// src/utils/embedAuth.js
//
// VP-18504: postMessage handshake that hands the va-portal JWT to the embedded
// ehr-frontend iframe. va-portal used to put the token in the iframe src
// (`?jwtToken=...`), which leaked it into server logs, browser history and
// Referer headers. Now the iframe asks its parent for the token instead:
//
//   ehr-frontend → parent : { source: 'ehr-frontend', type: 'va-portal:embed-request-token' }
//   va-portal    → iframe : { source: 'va-portal',    type: 'va-portal:embed-token', jwtToken }
//
// va-portal answers only to this iframe's window and only to the ehr-frontend
// origin (see va-portal's EhrSettingIframe.vue). The reply is accepted only when
// it comes from window.parent on a known va-portal origin, so a third-party page
// framing ehr-frontend can't plant its own session.

const REQUEST_TYPE = 'va-portal:embed-request-token';
const TOKEN_TYPE = 'va-portal:embed-token';

// Re-send the request until answered, in case the parent's listener isn't
// attached yet when the iframe boots.
const RETRY_INTERVAL_MS = 300;
const DEFAULT_TIMEOUT_MS = 5000;

const PARENT_ORIGINS =
  process.env.VUE_APP_ENV === 'production'
    ? ['https://portal.vibrant-wellness.com', 'https://www.vibrant-america.com']
    : [
        'https://staging.va-portal.pages.dev',
        'https://www.vibrant-america.com',
        'http://localhost:8080',
        'http://localhost:8084',
      ];

/**
 * Ask the va-portal parent window for the user's JWT.
 *
 * @param {number} [timeoutMs]
 * @returns {Promise<string|null>} the token, or null when not framed, the
 *   parent doesn't answer in time, or it answers with no token.
 */
export function requestEmbedToken(timeoutMs = DEFAULT_TIMEOUT_MS) {
  if (typeof window === 'undefined' || !window.parent || window.parent === window) {
    return Promise.resolve(null);
  }

  return new Promise((resolve) => {
    let retryTimer = null;
    let timeoutTimer = null;

    const finish = (token) => {
      window.removeEventListener('message', onMessage);
      clearInterval(retryTimer);
      clearTimeout(timeoutTimer);
      resolve(token || null);
    };

    function onMessage(event) {
      if (event.source !== window.parent) return;
      if (!PARENT_ORIGINS.includes(event.origin)) return;
      const data = event.data;
      if (!data || data.source !== 'va-portal' || data.type !== TOKEN_TYPE) return;
      finish(typeof data.jwtToken === 'string' ? data.jwtToken : null);
    }

    const sendRequest = () => {
      try {
        // The request carries no secret, so any target origin is fine; the
        // reply is the part that is origin-checked (both directions).
        window.parent.postMessage({ source: 'ehr-frontend', type: REQUEST_TYPE }, '*');
      } catch (err) {
        console.warn('embed-request-token postMessage failed', err);
      }
    };

    window.addEventListener('message', onMessage);
    sendRequest();
    retryTimer = setInterval(sendRequest, RETRY_INTERVAL_MS);
    timeoutTimer = setTimeout(() => {
      console.warn('embed token handshake timed out');
      finish(null);
    }, timeoutMs);
  });
}
